import { z } from 'zod'
import {
  add,
  startOfToday,
  isWeekend,
  isBefore,
  isSameDay,
  formatISO,
  parseISO,
} from 'date-fns/esm'

const Profile = z.object({
  firstName: z
    .string({ required_error: 'First Name is required' })
    .min(1, { message: 'First Name is required' })
    .max(300, { message: 'Maximum number of characters is 300' }),
  lastName: z
    .string({ required_error: 'Last Name is required' })
    .min(1, { message: 'Last Name is required' })
    .max(300, { message: 'Maximum number of characters is 300' }),
  email: z
    .string({ required_error: 'Email is required' })
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email' }),
  phone: z
    .string({ required_error: 'US Phone Number is required' })
    .min(1, { message: 'US Phone Number is required' })
    .regex(/^\+1[0-9]{10}$/, {
      message: 'US Phone Number is required',
    }),
})

const Billing = z.object({
  client: z
    .string({ required_error: 'Billing Party/Client Name is required' })
    .min(1, { message: 'Billing Party/Client Name is required' })
    .max(300, { message: 'Maximum number of characters is 300' }),
  reference: z
    .string({ required_error: 'Project Name/Reference is required' })
    .min(1, { message: 'Project Name/Reference is required' })
    .max(300, { message: 'Maximum number of characters is 300' }),
  cc: z.string().email({ message: 'Invalid email' }).nullable().optional().or(z.literal('')),
})

const RentalDate = z.preprocess(
  (arg) => {
    if (typeof arg == 'string') return parseISO(arg)
    if (arg instanceof Date) return new Date(arg)
  },
  z
    .date({ required_error: 'Please select a date' })
    .min(startOfToday(), { message: 'Please select a date on or after today' })
    .max(add(new Date(), { years: 1 }), { message: 'Please select a date within the next year' })
    .refine((date) => !isWeekend(date), {
      message: 'Please select a weekday (ACME is closed weekends)',
    })
)

export const Dates = z
  .object({
    startDate: RentalDate,
    endDate: RentalDate,
  })
  .refine(
    ({ startDate, endDate }) => {
      try {
        return isBefore(startDate, endDate) || isSameDay(startDate, endDate)
      } catch (_) {
        return false
      }
    },
    {
      message: 'The start date must come on or before the end date',
    }
  )
  .transform(({ startDate, endDate }) => ({
    startDate: formatISO(startDate, { representation: 'date' }),
    endDate: formatISO(endDate, { representation: 'date' }),
  }))

const Contact = z
  .string({ required_error: 'Contact Name is required' })
  .min(1, { message: 'Contact Name is required' })
  .max(300, { message: 'Maximum number of characters is 300' })

const Phone = z
  .string({ required_error: 'US Phone Number Contact is required' })
  .regex(/^\+1[0-9]{10}$/, {
    message: 'US Phone Number Contact is required',
  })

const Business = z
  .string({ required_error: 'Location Name is required' })
  .min(1, { message: 'Location Name is required' })
  .max(300, {
    message: 'Please shorten or abbreviate the location name to be fewer than 300 characters',
  })

const Street = z
  .string({ required_error: 'Address is required' })
  .min(1, { message: 'Street Address is required' })

const Unit = z
  .string()
  .max(300, { message: 'Please shorten or abbrieviate the unit to be fewer than 300 characters' })
  .nullable()
  .optional()

const Instructions = z
  .string()
  .max(2000, {
    message:
      'Please shorten your instructions to be fewer than 2000 characters (around 3-5 paragraphs)',
  })
  .nullable()
  .optional()

const City = z.enum(['Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island'], {
  errorMap: () => {
    return {
      message:
        'Delivery is only available to NYC boroughs (Manhattan, Brooklyn, Queens, The Bronx, and Staten Island)',
    }
  },
})

const Zip = z
  .string({ required_error: 'Please enter a valid NYC ZIP/Postal Code' })
  .regex(/^1[01][0-6][0-9][0-9](?:[-\s]\d{4})?$/, {
    message: 'Please enter a valid NYC ZIP/Postal Code',
  })

const DeliveryDetail = z.object({
  deliveryContact: Contact,
  deliveryPhone: Phone,
  deliveryBusiness: Business,
  deliveryAddress: Street,
  deliveryUnit: Unit,
  deliveryInstructions: Instructions,
  deliveryCity: City,
  deliveryZip: Zip,
})

const ReturnDetail = z.object({
  returnContact: Contact,
  returnPhone: Phone,
  returnBusiness: Business,
  returnAddress: Street,
  returnUnit: Unit,
  returnInstructions: Instructions,
  returnCity: City,
  returnZip: Zip,
})

const StartTime = z.enum(
  [
    // 'Anytime (10 AM - 4 PM)',
    'Morning (10 AM - 12 PM)',
    'Afternoon (12 PM - 2 PM)',
    'End of day (2 PM - 4 PM)',
  ],
  {
    errorMap: () => {
      return { message: 'Choose a valid time window for delivery' }
    },
  }
)

const EndTime = z.enum(
  [
    // 'Anytime (9 AM - 4 PM)',
    'Morning (9 AM - 12 PM)',
    'Afternoon (12 PM - 2 PM)',
    'End of day (2 PM - 4 PM)',
  ],
  {
    errorMap: () => {
      return { message: 'Choose a valid time window for return' }
    },
  }
)

const RoundTrip = z
  .object({
    deliveryType: z.literal('roundtrip'),
    startTime: StartTime,
    endTime: EndTime,
  })
  .merge(DeliveryDetail)

const MultiTrip = z
  .object({
    deliveryType: z.literal('multitrip'),
    startTime: StartTime,
    endTime: EndTime,
  })
  .merge(DeliveryDetail.merge(ReturnDetail))

const DeliveryOnly = z
  .object({
    deliveryType: z.literal('deliveryonly'),
    startTime: StartTime,
  })
  .merge(DeliveryDetail)

const ReturnOnly = z
  .object({
    deliveryType: z.literal('returnonly'),
    endTime: EndTime,
  })
  .merge(ReturnDetail)

const Delivery = z.discriminatedUnion(
  'deliveryType',
  [RoundTrip, MultiTrip, DeliveryOnly, ReturnOnly],
  {
    errorMap: () => {
      return { message: 'Choose a delivery method' }
    },
  }
)

const Pickup = z.object({
  shippingMethod: z.literal('pickup'),
})

const Courier = z.object({
  shippingMethod: z.literal('delivery'),
  shippingDetail: Delivery,
})

const Shipping = z.discriminatedUnion('shippingMethod', [Pickup, Courier], {
  errorMap: () => {
    return { message: 'Choose pickup or delivery' }
  },
})

const LongText = z
  .string()
  .max(2000, {
    message:
      'Please shorten your instructions to be fewer than 2000 characters (around 3-5 paragraphs)',
  })
  .nullable()
  .optional()

const Optional = z.object({
  comments: LongText,
  requests: LongText,
  suggestion: z.string().nullable().optional(),
  isUpdate: z.enum(['yes', 'no'], {
    errorMap: () => ({
      message:
        'Choose "Yes" if this is a new project, or "No" is you are updating a current request.',
    }),
  }),
})

export const ProfileAndBillingSchema = z.object({
  profile: Profile,
  billing: Billing,
})

export const DatesAndShippingSchema = z.object({
  dates: Dates,
  shipping: Shipping,
})

export const SubmissionSchema = z.object({
  profile: Profile,
  billing: Billing,
  dates: Dates,
  shipping: Shipping,
  optional: Optional,
})

export type TProfile = z.infer<typeof Profile>
export type TBilling = z.infer<typeof Billing>
export type TDates = z.infer<typeof Dates>
export type TShipping = z.infer<typeof Shipping>

export type TDelivery = Extract<TShipping, { shippingMethod: 'delivery' }>
export type TDeliveryDetail = z.infer<typeof DeliveryDetail> &
  z.infer<typeof ReturnDetail> & {
    startTime: z.infer<typeof StartTime>
    endTime: z.infer<typeof EndTime>
  }

export type ProfileAndBilling = z.infer<typeof ProfileAndBillingSchema>
export type DatesAndShipping = z.infer<typeof DatesAndShippingSchema>
export type Submission = z.infer<typeof SubmissionSchema>
